import QtySelector from "./qty-selector/QtySelector";
import Typography from "@mui/material/Typography";
import * as React from "react";
import {Dispatch, FC, SetStateAction, useCallback, useEffect, useMemo, useState} from "react";
import {IWinnerTicket, IWish, WishStatus, WishType} from "../../../../../store/wish/Helper";
import {WinnerEntry} from "../../../../../util/hooks/useWishCountdown";
import BuyVouchersDialog from "../../../../dialogs/buy-vouchers-dialog/BuyVouchersDialog";
import {useTranslation} from "react-i18next";
import {RoutePaths} from "../../../../../util/constants";
import {useLocation, useNavigate} from "react-router-dom";
import {RootState, useAppSelector} from "../../../../../store";
import ReactGA from "react-ga4";
import Box from "@mui/material/Box";
import {Swiper as SwiperType} from "swiper";
import TicketService from "../../../../../api/ticketService";
import ObtainEntriesDialog from "../../../../dialogs/obtain-entries-dialog/ObtainEntriesDialog";
import {toastError} from "../../../../shared/toast/Toast";
import {getSafeBackendErrorMessage} from "../../../../../util/utils";
import TranslateSafeLoadingButton from "../../../../shared/translate-safe-loading-button/TranslateSafeLoadingButton";
import ObtainFreeVoucherDialog from "../../../../dialogs/obtain-free-voucher-dialog/ObtainEntriesDialog";
import {TicketStatus} from "../../../../client-panel/vouchers/vouchers-filter/VouchersFilterHelper";
import {IUserTicket} from "../../../../../store/ticket/Helper";
import {metaTrack, metaTrackCustom} from "../../../../../util/metaPixel";
import useWishTicketAvailability from "../../../../shared/tickets-count-linear-progress/useWishTicketAvailability";

interface IProps {
    countDown: null | number
    drawStarted: boolean
    winners: IWinnerTicket[] | WinnerEntry[] | null
    wish: IWish
    color: string
    qty: any
    setQty: Dispatch<SetStateAction<any>>
    options: IWish[]
    buttonSize?: 'small' | 'medium' | 'large'
    swiperInstance?: SwiperType
    activeWish?: IWish
    accumulateTotals?: boolean
    isFromDetails?: boolean
}

type PostLoginCheckoutDialog = "obtainEntries" | "buyVouchers" | "obtainFreeVoucher";

interface IPostLoginCheckoutState {
    wishId: string
    dialog: PostLoginCheckoutDialog
    qty: number
}

const POST_LOGIN_CHECKOUT_STATE_KEY = "postLoginCheckout";

const normalizeCheckoutQty = (value: unknown): number => {
    const parsedQty = Number(value)
    if (!Number.isFinite(parsedQty) || parsedQty < 1) {
        return 1
    }

    return Math.floor(parsedQty)
}

const generateOrderId = (): string => {
    const alphabet = "ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789";
    let result = "";
    const bytes = new Uint8Array(10);
    crypto.getRandomValues(bytes);
    for (let i = 0; i < 10; i++) {
        // choose index 0–35
        result += alphabet[bytes[i] % alphabet.length];
    }
    return result;
}

const MakeAWishGrid: FC<IProps> = ({
                                       buttonSize = 'large',
                                       accumulateTotals = false,
                                       isFromDetails = false,
                                       ...props
                                   }) => {
    const isLoggedIn = useAppSelector((state: RootState) => !!state.authReducer.loggedInUser.id)
    const isAdmin = useAppSelector((state: RootState) => state.authReducer.loggedInUser.is_admin)
    const isClient = useAppSelector((state: RootState) => !!state.authReducer.loggedInUser.client_id)
    const serverTime = useAppSelector((state: RootState) => state.appReducer.serverTime)
    const {t} = useTranslation()
    const [openBuyVouchers, setOpenBuyVouchers] = useState(false)
    const [openObtainEntries, setOpenObtainEntries] = useState(false)
    const [openObtainFreeVoucher, setOpenObtainFreeVoucher] = useState(false)
    const navigate = useNavigate()
    const location = useLocation()
    const {pathname, search, hash} = location
    const [hasParticipated, setHasParticipated] = useState(false)
    const [hasResolvedParticipation, setHasResolvedParticipation] = useState(props.wish.cost !== 0)
    const [loading, setLoading] = useState(false)
    const orderId = generateOrderId()
    const wishId = props.wish.id
    const setQty = props.setQty
    const locationState = location.state && typeof location.state === "object"
        ? location.state as Record<string, unknown>
        : null

    const {remaining} = useWishTicketAvailability({
        wish: props.wish,
        accumulateTotals
    })
    const selectedQty = useMemo(() => normalizeCheckoutQty(props.qty), [props.qty])

    const checkoutDialog = useMemo<PostLoginCheckoutDialog>(() => {
        if (props.wish.type === WishType.VOUCHER) {
            return props.wish.cost !== 0 ? "buyVouchers" : "obtainFreeVoucher"
        }

        return "obtainEntries"
    }, [props.wish.cost, props.wish.type])

    const pendingCheckout = useMemo<IPostLoginCheckoutState | null>(() => {
        const candidate = locationState?.[POST_LOGIN_CHECKOUT_STATE_KEY]

        if (!candidate || typeof candidate !== "object") {
            return null
        }

        const {wishId, dialog, qty} = candidate as Partial<IPostLoginCheckoutState>

        if (
            !wishId ||
            !dialog ||
            !["obtainEntries", "buyVouchers", "obtainFreeVoucher"].includes(dialog) ||
            !Number.isFinite(Number(qty))
        ) {
            return null
        }

        return {
            wishId,
            dialog,
            qty: normalizeCheckoutQty(qty)
        }
    }, [locationState])

    const openCheckoutDialog = useCallback((dialog: PostLoginCheckoutDialog) => {
        props.swiperInstance?.autoplay?.stop();
        if (dialog === "buyVouchers") {
            setOpenBuyVouchers(true)
            return
        }
        if (dialog === "obtainFreeVoucher") {
            setOpenObtainFreeVoucher(true)
            return
        }
        setOpenObtainEntries(true)
    }, [props.swiperInstance])

    const clearPendingCheckoutState = useCallback(() => {
        if (!locationState?.[POST_LOGIN_CHECKOUT_STATE_KEY]) {
            return
        }

        const nextState = {...locationState}
        delete nextState[POST_LOGIN_CHECKOUT_STATE_KEY]
        navigate(`${pathname}${search}${hash}`, {
            replace: true,
            state: Object.keys(nextState).length > 0 ? nextState : undefined
        })
    }, [hash, locationState, navigate, pathname, search])

    useEffect(() => {
        if (!props.wish.id) {
            setLoading(false)
            setHasResolvedParticipation(true)
            return
        }

        if (props.wish.cost === 0 && isLoggedIn && !isAdmin && !isClient) {
            setHasResolvedParticipation(false)
            setLoading(true)
            TicketService.fetchTicketsForUser(props.wish.id)
                .then(res => {
                    setHasParticipated(res.data.some((t: IUserTicket) => t.status === TicketStatus.ACTIVE))
                })
                .catch(() => {
                    // Avoid unhandled promise rejections from transient API errors.
                    setHasParticipated(false)
                })
                .finally(() => {
                    setLoading(false)
                    setHasResolvedParticipation(true)
                })
            return
        }

        setLoading(false)
        setHasResolvedParticipation(true)
        if (!isLoggedIn) {
            setHasParticipated(false)
        }
    }, [props.wish.cost, props.wish.id, isLoggedIn, isAdmin, isClient]);

    const isCheckoutEvaluationReady = useMemo(() => (
        serverTime !== null && hasResolvedParticipation
    ), [hasResolvedParticipation, serverTime])

    const canOpenCheckout = useMemo(() => (
        isLoggedIn &&
        !isAdmin &&
        !isClient &&
        props.wish.status !== WishStatus.PENDING &&
        props.wish.status !== WishStatus.FINISHED &&
        !props.wish.name_en.toLowerCase().includes('coming soon') &&
        props.countDown !== null &&
        props.countDown >= 0 &&
        !props.winners?.length &&
        !props.drawStarted &&
        !hasParticipated &&
        remaining > 0
    ), [
        hasParticipated,
        isAdmin,
        isClient,
        isLoggedIn,
        props.countDown,
        props.drawStarted,
        props.winners,
        props.wish.name_en,
        props.wish.status,
        remaining
    ])

    const isCheckoutDefinitelyBlocked = useMemo(() => (
        isCheckoutEvaluationReady && !canOpenCheckout
    ), [canOpenCheckout, isCheckoutEvaluationReady])

    useEffect(() => {
        if (!pendingCheckout || pendingCheckout.wishId !== wishId || !isLoggedIn || !isCheckoutEvaluationReady) {
            return
        }

        if (pendingCheckout.dialog === checkoutDialog && canOpenCheckout) {
            const restoredQty = Math.max(1, Math.min(pendingCheckout.qty, remaining))

            if (selectedQty !== restoredQty) {
                setQty(restoredQty)
                return
            }

            openCheckoutDialog(checkoutDialog)
            clearPendingCheckoutState()
            return
        }

        if (isCheckoutDefinitelyBlocked) {
            clearPendingCheckoutState()
        }
    }, [
        canOpenCheckout,
        checkoutDialog,
        clearPendingCheckoutState,
        isCheckoutDefinitelyBlocked,
        isCheckoutEvaluationReady,
        isLoggedIn,
        openCheckoutDialog,
        pendingCheckout,
        setQty,
        remaining,
        selectedQty,
        wishId
    ])

    const handleOpenBuyTickets = useCallback(async () => {
        if (props.wish.status !== WishStatus.PENDING) {
            if (isLoggedIn) {
                ReactGA.event({
                    category: 'Wish',
                    action: 'make_a_wish_click_start',
                    label: `Make a wish - Start - ${props.wish.id}`,
                })
                metaTrack("InitiateCheckout", {
                    content_ids: [props.wish.id],
                    content_name: props.wish.name_en,
                    content_type: props.wish.type,
                    currency: "MKD",
                    num_items: selectedQty,
                    value: selectedQty * (props.wish.cost ?? 0),
                });
                if (props.wish.type === WishType.VOUCHER) {
                    try {
                        openCheckoutDialog(checkoutDialog)
                    } catch (e: any) {
                        toastError(getSafeBackendErrorMessage(e.response?.data?.detail))
                    }
                } else {
                    openCheckoutDialog(checkoutDialog)
                }

                return
            }
            metaTrackCustom("WishCheckoutBlockedUnauthenticated", {
                content_id: props.wish.id,
                content_name: props.wish.name_en,
                content_type: props.wish.type,
                from_path: pathname,
            });
            navigate(RoutePaths.AUTH, {
                state: {
                    redirectTo: `${pathname}${search}${hash}`,
                    [POST_LOGIN_CHECKOUT_STATE_KEY]: {
                        wishId: props.wish.id,
                        dialog: checkoutDialog,
                        qty: selectedQty
                    }
                }
            });
        }
    }, [
        checkoutDialog,
        hash,
        navigate,
        props.wish.status,
        props.wish.id,
        props.wish.name_en,
        props.wish.type,
        props.wish.cost,
        openCheckoutDialog,
        isLoggedIn,
        pathname,
        selectedQty,
        search
    ])

    const handleCloseBuyVouchersDialog = async () => {
        setOpenBuyVouchers(false)
    }

    const fontVariant = useMemo(() => buttonSize === 'small' ? 'subtitle2' : 'subtitle1', [buttonSize])
    const isEntryWish = props.wish.type === WishType.DRAW || props.wish.type === WishType.BUNDLE
    const isRecommended = props.wish.recommended || isEntryWish

    if (props.wish.name_en.toLowerCase().includes('coming soon')) {
        return <Typography variant={fontVariant} fontWeight="600" sx={{color: props.color}} textAlign="center"
                           width="100%" p={isFromDetails ? 0 : 1}>
            {t('prize_stay_tuned')}
        </Typography>
    }

    if (props.wish.status === WishStatus.FINISHED) {
        return <Typography variant={fontVariant} fontWeight="600" sx={{color: props.color}} textAlign="center"
                           width="100%" p={isFromDetails ? 0 : 1}>
            {t('wish_has_finished')}
        </Typography>
    }

    if (isAdmin || isClient || !props.countDown || props.countDown < 0 || props.winners?.length) {
        return null
    }

    if (props.drawStarted) {
        return isEntryWish ?
            <Typography variant={fontVariant} fontWeight="600" sx={{color: props.color}} textAlign="center"
                        width="100%" p={isFromDetails ? 0 : 1}>
                {t('await_wish_draw')}
            </Typography> : null
    }

    if (hasParticipated) {
        return <Typography variant={fontVariant} fontWeight="600" sx={{color: props.color}} textAlign="center"
                           width="100%" p={isFromDetails ? 0 : 1}>
            {isEntryWish ? t('already_participated') : t('already_obtained_voucher')}
        </Typography>
    }

    if (remaining > 0) {
        return <Box sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'nowrap',
            gap: '0.5em',
            width: '100%',
            p: isFromDetails ? 0 : 1
        }}>
            {props.wish.cost !== 0 && (!props.wish.children?.length || !accumulateTotals) &&
                <QtySelector qty={props.qty} setQty={props.setQty}
                             max={remaining}
                             recommended={isRecommended} size={buttonSize}
                             swiperInstance={props.swiperInstance}/>}

            <TranslateSafeLoadingButton loading={loading} fullWidth color={isRecommended ? 'tertiary' : 'primary'}
                           variant='contained'
                           onClick={handleOpenBuyTickets} size={buttonSize}
                                        sx={{
                                            whiteSpace: 'nowrap',
                                            minWidth: 0,
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis'
                                        }}>{isEntryWish ? t('make_a_wish') : props.wish.cost !== 0 ? t('buy_voucher', {count: props.qty}) : t('obtain_voucher')}</TranslateSafeLoadingButton>
            {props.wish.type === WishType.VOUCHER ? props.wish.cost !== 0 ? <BuyVouchersDialog open={openBuyVouchers}
                                                                                               handleDialogClose={handleCloseBuyVouchersDialog}
                                                                                               resetTicketCount={() => props.setQty(1)}
                                                                                               wish={props.wish}
                                                                                               count={props.qty}
                                                                                               setCount={props.setQty}
                                                                                               swiperInstance={props.swiperInstance}
                                                                                               recommended={isRecommended}
                                                                                               setHasParticipated={setHasParticipated}
                                                                                               orderId={orderId}
                                                                                               options={props.options}
                                                                                               activeWish={props.activeWish}/> :
                    <ObtainFreeVoucherDialog open={openObtainFreeVoucher}
                                             handleDialogClose={() => setOpenObtainFreeVoucher(false)} wish={props.wish}
                                             hasParticipated={hasParticipated}
                                             setHasParticipated={setHasParticipated}/> :
                <ObtainEntriesDialog open={openObtainEntries}
                                     handleDialogClose={() => setOpenObtainEntries(false)}
                                     resetTicketCount={() => props.setQty(1)}
                                     wish={props.wish} count={props.qty} orderId={orderId}
                                     setCount={props.setQty}/>}
        </Box>
    }

    return <Typography variant={fontVariant} fontWeight="600" sx={{color: props.color}} textAlign="center"
                       width="100%" p={isFromDetails ? 0 : 1}>
        {props.wish.type === WishType.VOUCHER ? t('all_vouchers_bought') : t('all_entries_obtained')}
    </Typography>

}

export default MakeAWishGrid
